import React, { useState, useEffect } from 'react';
import { getEmailTemplates, updateEmailTemplate, createEmailTemplate, deleteEmailTemplate, sendTestEmail } from '../../services/emailService';
import { emailTemplateLibrary } from '../../data/emailTemplateLibrary';
import TemplatePreviewModal from '../../components/admin/TemplatePreviewModal';
import TemplateCard from '../../components/admin/TemplateCard';
import SafeIcon from '../../common/SafeIcon';

const EmailTemplates = () => {
  const [activeTab, setActiveTab] = useState('library');
  const [dbTemplates, setDbTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState(null);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    name: '', slug: '', subject: '', html_content: '',
    variables: [], category: 'system', is_active: true
  });

  useEffect(() => { if (activeTab === 'database') loadDbTemplates(); }, [activeTab]);

  const loadDbTemplates = async () => {
    setLoading(true);
    try {
      const result = await getEmailTemplates();
      if (result.success) setDbTemplates(result.data);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditing(template);
    setFormData({
      name: template.name, slug: template.slug, subject: template.subject,
      html_content: template.html_content, variables: template.variables || [],
      category: template.category, is_active: template.is_active
    });
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const result = await updateEmailTemplate(editing.id, formData);
        if (result.success) setDbTemplates(dbTemplates.map(t => t.id === editing.id ? result.data : t));
      } else {
        const result = await createEmailTemplate(formData);
        if (result.success) setDbTemplates([result.data, ...dbTemplates]);
      }
      handleCancel();
    } catch (err) {
      console.error('Error saving template:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this template?')) return;
    try {
      const result = await deleteEmailTemplate(id);
      if (result.success) setDbTemplates(dbTemplates.filter(t => t.id !== id));
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    setTestStatus({ type: 'sending', message: 'Sending test email...' });
    try {
      const result = await sendTestEmail(testEmail);
      setTestStatus({
        type: result.success ? 'success' : 'error',
        message: result.success ? `Test email sent to ${testEmail}!` : (result.error || 'Failed to send')
      });
    } catch (err) {
      setTestStatus({ type: 'error', message: err.message });
    }
    setTimeout(() => setTestStatus(null), 6000);
  };

  const handleCancel = () => {
    setEditing(null);
    setShowCreate(false);
    setFormData({ name: '', slug: '', subject: '', html_content: '', variables: [], category: 'system', is_active: true });
  };

  const supabaseTemplates = emailTemplateLibrary.filter(t => t.isSupabaseTemplate);
  const appTemplates = emailTemplateLibrary.filter(t => t.isAppTemplate);

  const filteredLibrary = filterType === 'all'
    ? emailTemplateLibrary
    : filterType === 'supabase'
      ? supabaseTemplates
      : appTemplates;

  const categoryColors = {
    system: 'bg-blue-100 text-blue-700',
    transactional: 'bg-purple-100 text-purple-700',
    marketing: 'bg-orange-100 text-orange-700'
  };

  if (editing || showCreate) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{editing ? 'Edit Template' : 'Create Template'}</h2>
            <p className="text-gray-500 mt-1">{editing ? 'Update email template details' : 'Create a new email template'}</p>
          </div>
          <button onClick={handleCancel} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <SafeIcon name="X" className="text-xl" />
          </button>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Template Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Welcome Email" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Slug</label>
              <input type="text" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="welcome" disabled={!!editing}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none disabled:bg-gray-50" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Subject</label>
            <input type="text" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Welcome to DayClap! ❤️" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none">
                <option value="system">System</option>
                <option value="transactional">Transactional</option>
                <option value="marketing">Marketing</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select value={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none">
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">HTML Content</label>
            <textarea value={formData.html_content} onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
              placeholder="<div>...</div>" rows={14}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none font-mono text-sm" />
          </div>
          <div className="flex gap-3 pt-4">
            <button onClick={handleSave}
              className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-pink-200 hover:shadow-xl transition-all">
              {editing ? 'Update Template' : 'Create Template'}
            </button>
            <button onClick={handleCancel} className="px-8 bg-gray-100 text-gray-700 font-medium py-3.5 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Templates</h2>
          <p className="text-gray-500 mt-1">Preview, copy, and manage email templates</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowTestPanel(!showTestPanel)}
            className="bg-emerald-600 text-white font-bold px-5 py-3 rounded-xl hover:bg-emerald-700 transition-all flex items-center gap-2 text-sm">
            <SafeIcon name="Send" className="text-sm" /> Test Email
          </button>
        </div>
      </div>

      {/* How It Works Banner */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-5 border border-gray-200">
        <h3 className="font-bold text-gray-800 text-sm mb-3">How email templates work in DayClap:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                <SafeIcon name="Shield" className="text-xs" />
                Supabase Auth Templates
              </span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-2">
              Sent automatically by Supabase for signup confirmation, password reset, magic link, and email change.
            </p>
            <p className="text-xs text-emerald-600 font-semibold">
              Variables: <code className="bg-emerald-50 px-1 rounded">{'{{ .ConfirmationURL }}'}</code>, <code className="bg-emerald-50 px-1 rounded">{'{{ .Token }}'}</code>, <code className="bg-emerald-50 px-1 rounded">{'{{ .Email }}'}</code>
            </p>
            <p className="text-xs text-gray-400 mt-1">→ Paste into Supabase Dashboard → Auth → Email Templates</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-violet-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 flex items-center gap-1">
                <SafeIcon name="Zap" className="text-xs" />
                App-Sent Templates (Emailit API)
              </span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-2">
              Sent from the app for invitations, notifications, reminders, etc. via the send-email edge function.
            </p>
            <p className="text-xs text-violet-600 font-semibold">
              Variables: <code className="bg-violet-50 px-1 rounded">{'{{sender_name}}'}</code>, <code className="bg-violet-50 px-1 rounded">{'{{recipient_name}}'}</code>, <code className="bg-violet-50 px-1 rounded">{'{{occasion}}'}</code>
            </p>
            <p className="text-xs text-gray-400 mt-1">→ Stored in email_templates DB table, sent via edge function</p>
          </div>
        </div>
      </div>

      {/* Test Panel */}
      {showTestPanel && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-emerald-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <SafeIcon name="Send" className="text-emerald-600 text-sm" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-sm">Send Test Email</h3>
              <p className="text-xs text-gray-400">Verify Emailit API email delivery is working</p>
            </div>
          </div>
          <div className="flex gap-3">
            <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email address..." className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm" />
            <button onClick={handleSendTest} disabled={!testEmail || testStatus?.type === 'sending'}
              className="bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm">
              {testStatus?.type === 'sending' ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : <SafeIcon name="Send" className="text-sm" />}
              Send
            </button>
          </div>
          {testStatus && testStatus.type !== 'sending' && (
            <div className={`mt-3 p-3 rounded-xl text-sm flex items-center gap-2 ${
              testStatus.type === 'success' ? 'bg-green-50 border border-green-100 text-green-700' : 'bg-red-50 border border-red-100 text-red-700'
            }`}>
              <SafeIcon name={testStatus.type === 'success' ? 'CheckCircle' : 'AlertCircle'} className="flex-shrink-0" />
              {testStatus.message}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-1.5 flex gap-1 w-fit">
        {[
          { key: 'library', label: '📚 Template Library', count: emailTemplateLibrary.length },
          { key: 'database', label: '🗄️ Database Templates', count: dbTemplates.length }
        ].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === tab.key
                ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}>
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              activeTab === tab.key ? 'bg-white/20' : 'bg-gray-200'
            }`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Library Tab */}
      {activeTab === 'library' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Browse all templates, preview them, and copy the HTML.
            </p>
            <div className="flex gap-2">
              {[
                { key: 'all', label: 'All', count: emailTemplateLibrary.length },
                { key: 'supabase', label: '🛡️ Supabase Auth', count: supabaseTemplates.length },
                { key: 'app', label: '⚡ App-Sent', count: appTemplates.length }
              ].map((filter) => (
                <button key={filter.key} onClick={() => setFilterType(filter.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                    filterType === filter.key
                      ? 'bg-pink-100 text-pink-700'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}>
                  {filter.label}
                  <span className={`text-xs px-1 py-0.5 rounded-full ${
                    filterType === filter.key ? 'bg-pink-200' : 'bg-gray-200'
                  }`}>{filter.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Supabase Section Header */}
          {(filterType === 'all' || filterType === 'supabase') && (
            <div className="mt-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-emerald-200" />
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                  🛡️ Supabase Auth Templates — Paste into Supabase Dashboard
                </span>
                <div className="h-px flex-1 bg-emerald-200" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {supabaseTemplates.map((template) => (
                  <TemplateCard key={template.id} template={template} onPreview={setPreviewTemplate} />
                ))}
              </div>
            </div>
          )}

          {/* App-Sent Section Header */}
          {(filterType === 'all' || filterType === 'app') && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-violet-200" />
                <span className="text-xs font-bold text-violet-700 bg-violet-50 px-3 py-1 rounded-full border border-violet-200">
                  ⚡ App-Sent Templates — Sent via Emailit API Edge Function
                </span>
                <div className="h-px flex-1 bg-violet-200" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {appTemplates.map((template) => (
                  <TemplateCard key={template.id} template={template} onPreview={setPreviewTemplate} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Database Tab */}
      {activeTab === 'database' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Templates stored in your Supabase database for the Emailit API edge function.</p>
            <button onClick={() => setShowCreate(true)}
              className="bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold px-5 py-2.5 rounded-xl shadow-lg shadow-pink-200 hover:shadow-xl transition-all flex items-center gap-2 text-sm">
              <SafeIcon name="Plus" className="text-sm" /> Create Template
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
            </div>
          ) : dbTemplates.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <SafeIcon name="Mail" className="text-4xl text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No database templates yet</p>
              <p className="text-gray-300 text-sm mt-1">Create one or copy from the Template Library</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
              {dbTemplates.map((template) => (
                <div key={template.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-gray-800 text-lg mb-1">{template.name}</h3>
                      <code className="text-xs text-gray-400 font-mono bg-gray-50 px-2 py-1 rounded">{template.slug}</code>
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                      template.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>{template.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.subject}</p>
                  <div className="flex items-center gap-2 mb-5">
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${categoryColors[template.category] || 'bg-gray-100 text-gray-600'}`}>
                      {template.category}
                    </span>
                    {template.variables?.length > 0 && (
                      <span className="text-xs text-gray-400">{template.variables.length} vars</span>
                    )}
                  </div>
                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <button onClick={() => handleEdit(template)}
                      className="flex-1 text-sm text-blue-600 hover:text-blue-800 font-semibold py-2.5 rounded-lg hover:bg-blue-50 transition-colors">Edit</button>
                    <button onClick={() => handleDelete(template.id)}
                      className="flex-1 text-sm text-red-500 hover:text-red-700 font-semibold py-2.5 rounded-lg hover:bg-red-50 transition-colors">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      )}
    </div>
  );
};

export default EmailTemplates;